import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';

import { cn } from '@/lib/cn';
import { motionSafe, springSheet } from '@/lib/motion';
import { useTasks } from '@/features/tasks/useTasks';
import { formatDuration } from '@/features/tasks/tree';

/**
 * Fuzzy task picker for switching mid-session.
 *
 * Opens on `S`, filters as you type, commits on Enter. The point is that
 * changing what you are working on costs under a second -- the alternative to
 * an easy switch is not focus, it is dropping the thread.
 */
export function TaskSwitcher({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (taskId: number) => void;
}) {
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const tasks = useTasks();

  const matches = useMemo(() => {
    const needle = query.toLowerCase().trim();
    const all = tasks.data ?? [];
    if (!needle) return all.slice(0, 8);
    return all.filter((t) => t.title.toLowerCase().includes(needle)).slice(0, 8);
  }, [query, tasks.data]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setIndex(0);
  }, [query]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIndex((i) => Math.min(i + 1, matches.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const picked = matches[index];
      if (picked) onPick(picked.id);
    }
  };

  return (
    <div
      className="fixed inset-0 z-overlay flex items-start justify-center p-4 pt-[12vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Switch task"
    >
      {/* Scrim: this one is blocking, so the background is pushed back. */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={motionSafe({ duration: 0.15 })}
        onClick={onClose}
        className="absolute inset-0 bg-scrim"
      />

      <motion.div
        initial={{ opacity: 0, y: -8, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.98 }}
        transition={motionSafe(springSheet)}
        className="relative w-full max-w-lg overflow-hidden rounded-xl border border-border bg-surface-1 shadow-sheet"
      >
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Switch to…"
          aria-label="Search tasks"
          className="min-h-touch w-full bg-transparent px-4 text-body outline-none focus-visible:ring-2 focus-visible:ring-ring placeholder:text-text-tertiary"
        />

        <ul className="max-h-72 overflow-y-auto border-t border-border">
          {matches.length === 0 && (
            <li className="px-4 py-3 text-small text-text-tertiary">No matching tasks.</li>
          )}
          {matches.map((task, i) => (
            <li key={task.id}>
              <button
                type="button"
                onClick={() => onPick(task.id)}
                onMouseEnter={() => setIndex(i)}
                className={cn(
                  'flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left',
                  i === index ? 'bg-surface-2' : 'hover:bg-surface-2',
                )}
              >
                <span className="min-w-0 flex-1 truncate text-body">{task.title}</span>
                {task.estimate_minutes && (
                  <span className="tabular shrink-0 text-small text-text-tertiary">
                    {formatDuration(task.estimate_minutes)}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </motion.div>
    </div>
  );
}
